/**
 * Sanitize untrusted email HTML before dangerouslySetInnerHTML (no DOMPurify).
 * Allows common message markup; blocks script, style, data: URLs and overlays.
 */
const DROP_TAGS =
  "script, iframe, object, embed, form, link, meta, base, style, svg, math, video, audio, canvas, frame, frameset, applet, template";

const URL_ATTRS = new Set([
  "href",
  "src",
  "xlink:href",
  "poster",
  "action",
  "formaction",
  "background",
  "cite",
]);

const SHADOW_ROOT_ATTRS = new Set([
  "shadowroot",
  "shadowrootmode",
  "shadowrootdelegatesfocus",
  "shadowrootclonable",
  "shadowrootserializable",
]);

function stripInvisibleChars(value) {
  return String(value || "")
    .split("")
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      return (
        code > 31 &&
        code !== 127 &&
        code !== 0xfffd &&
        !(code >= 0x200b && code <= 0x200d) &&
        code !== 0xfeff
      );
    })
    .join("");
}

function isAllowedUrl(value, { allowMailto = false, allowCid = false } = {}) {
  const raw = stripInvisibleChars(value).trim();
  if (!raw) return false;
  if (raw.startsWith("#") && !raw.includes(":")) return true;

  // Protocol-relative URLs in HTML email are https-equivalent for allowlisting.
  const forParse = raw.startsWith("//") ? `https:${raw}` : raw;
  let parsed;
  try {
    parsed = new URL(forParse);
  } catch {
    return false;
  }

  if (parsed.protocol === "http:" || parsed.protocol === "https:") return true;
  if (allowMailto && (parsed.protocol === "mailto:" || parsed.protocol === "tel:")) return true;
  if (allowCid && parsed.protocol === "cid:") return true;
  return false;
}

function stripUnsafeTagsRegex(html) {
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, "")
    .replace(/<template[\s\S]*?>[\s\S]*?<\/template>/gi, "")
    .replace(
      /<(object|embed|form|svg|math|link|meta|base|video|audio|canvas)[\s\S]*?>[\s\S]*?<\/\1>/gi,
      ""
    )
    .replace(/<(object|embed|link|meta|base|source|track|template)[^>]*>/gi, "")
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/\sstyle\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/\s(?:ping|target|rel)\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(
      /\sshadowroot(?:mode|delegatesfocus|clonable|serializable)?\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi,
      ""
    )
    .replace(
      /\s(href|src|xlink:href|poster|action|formaction|srcset|background|cite)\s*=\s*("?\s*(?:javascript|vbscript|data|file):[^"]*"|'?\s*(?:javascript|vbscript|data|file):[^']*'|(?:javascript|vbscript|data|file):[^\s>]+)/gi,
      ""
    );
}

function sanitizeElement(el) {
  [...el.attributes].forEach((attr) => {
    const name = attr.name.toLowerCase();
    const value = attr.value || "";
    if (
      name.startsWith("on") ||
      name === "srcdoc" ||
      name === "style" ||
      name === "srcset" ||
      name === "ping" ||
      SHADOW_ROOT_ATTRS.has(name)
    ) {
      el.removeAttribute(attr.name);
      return;
    }
    if (URL_ATTRS.has(name)) {
      const allowMailto = name === "href";
      const allowCid = name === "src" && el.tagName === "IMG";
      if (!isAllowedUrl(value, { allowMailto, allowCid })) {
        el.removeAttribute(attr.name);
      }
    }
  });

  if (el.tagName === "A" || el.tagName === "AREA") {
    const target = (el.getAttribute("target") || "").trim().toLowerCase();
    // Named targets (_new, custom names) keep window.opener unless rel=noopener.
    if (target && target !== "_self") {
      el.setAttribute("rel", "noopener noreferrer");
    } else {
      el.removeAttribute("rel");
    }
  }
}

function sanitizeFragment(root) {
  // Nested <template> documents are not covered by querySelectorAll on this root.
  // SVG/MathML <template> is not HTMLTemplateElement and has no .content.
  [...root.querySelectorAll("template")].forEach((tpl) => {
    if (tpl.content) {
      sanitizeFragment(tpl.content);
    }
    tpl.remove();
  });
  root.querySelectorAll(DROP_TAGS).forEach((el) => el.remove());
  root.querySelectorAll("*").forEach((el) => sanitizeElement(el));
}

export function sanitizeHtml(html) {
  const raw = html == null ? "" : String(html);
  if (!raw.trim()) return "";
  if (typeof document === "undefined") {
    return stripUnsafeTagsRegex(raw);
  }
  const template = document.createElement("template");
  template.innerHTML = raw;
  sanitizeFragment(template.content);
  return template.innerHTML;
}

/** Outlook/Word often store a whole HTML document as `&lt;!-- @font-face …`. */
export function looksLikeEscapedHtml(value) {
  return /&lt;\s*(?:!--|[a-z!/?])/i.test(String(value || ""));
}

export function decodeHtmlEntitiesOnce(value) {
  const raw = String(value || "");
  if (!raw) return "";
  if (typeof document !== "undefined") {
    const ta = document.createElement("textarea");
    ta.innerHTML = raw;
    return ta.value;
  }
  return raw
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&");
}

/** Strip Word/Outlook comments and CSS that leaked into visible text. */
export function stripMailerChrome(html) {
  let out = String(html || "");
  out = out.replace(/<!--[\s\S]*?-->/g, "");
  out = out.replace(/<!\[if[\s\S]*?<!\[endif\]>/gi, "");
  out = out.replace(/@font-face\s*\{[\s\S]*?\}/gi, "");
  out = out.replace(/@page\s*[^{]*\{[\s\S]*?\}/gi, "");
  out = out.replace(/@media[^{]*\{[\s\S]*?\}/gi, "");
  return out;
}

/** Sanitize + unwrap escaped Outlook HTML for on-screen mail. */
export function prepareInboundEmailHtml(html) {
  let raw = html == null ? "" : String(html);
  if (!raw.trim()) return "";
  if (looksLikeEscapedHtml(raw)) {
    raw = decodeHtmlEntitiesOnce(raw);
  }
  return sanitizeHtml(stripMailerChrome(raw));
}

/** Decode escaped tags then sanitize — for AI summaries and other rich HTML. */
export function prepareDisplayHtml(html) {
  let raw = html == null ? "" : String(html);
  if (!raw.trim()) return "";
  if (looksLikeEscapedHtml(raw)) {
    raw = decodeHtmlEntitiesOnce(raw);
  }
  return sanitizeHtml(raw);
}

export function inboundEmailPlainText(html, text, limit = 160) {
  const prepared = prepareInboundEmailHtml(html);
  let plain = "";
  if (prepared && typeof document !== "undefined") {
    const div = document.createElement("div");
    div.innerHTML = prepared;
    plain = (div.textContent || "").replace(/\s+/g, " ").trim();
  }
  if (!plain) {
    const fallback = String(text || "");
    const source = looksLikeEscapedHtml(fallback)
      ? decodeHtmlEntitiesOnce(fallback)
      : fallback;
    const cleaned = stripMailerChrome(source).replace(/<[^>]+>/g, " ");
    if (typeof document !== "undefined") {
      const div = document.createElement("div");
      div.innerHTML = cleaned;
      plain = (div.textContent || "").replace(/\s+/g, " ").trim();
    } else {
      plain = cleaned.replace(/\s+/g, " ").trim();
    }
  }
  if (limit && plain.length > limit) return `${plain.slice(0, limit).trim()}…`;
  return plain;
}
